import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthChange } from './utils/firebase';
import './styles/globals.css';
import Login          from './pages/Login';
import LandingPage    from './pages/LandingPage';
import LegalPage      from './pages/LegalPage';
import Dashboard      from './pages/Dashboard';
import HomePage       from './pages/HomePage';
import BranchPage from './pages/BranchPage';
import StockPage from './pages/StockPage';
import DebtorsPage from './pages/DebtorsPage';
import CreditorsPage from './pages/CreditorsPage';
import SalesPage from './pages/SalesPage';
import StockOptimisation from './pages/StockOptimisation';
import GroupPL           from './pages/GroupPL';
import StorePerformance  from './pages/StorePerformance';
import BalanceSheet      from './pages/BalanceSheet';
import ExpenseAnalysis   from './pages/ExpenseAnalysis';
import CashFlow          from './pages/CashFlow';
import Notes             from './pages/Notes';
import GroupConsolidated from './pages/GroupConsolidated';
import ReportRender      from './pages/ReportRender';
import AlertPreferences  from './pages/AlertPreferences';
import { Advisory, ActionTracking }                          from './pages/Governance';
import Advisor                                               from './pages/Advisor';
import Layout                                                from './components/Layout';
import OnboardWizard                                         from './pages/admin/OnboardWizard';
import AdminClients                                          from './pages/admin/AdminClients';
import AdminClientDetail                                     from './pages/admin/AdminClientDetail';
import QAAgent                                               from './pages/QAAgent';
import AcceptInvite                                          from './pages/AcceptInvite';
import CompanySelector                                       from './pages/CompanySelector';
import { CompanyProvider, useCompany }                       from './context/CompanyContext';

function AppInner() {
  const [user,    setUser]    = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [claims,  setClaims]  = useState(null);
  const { loadForCurrentUser, clearAllCompanyData, companyKey } = useCompany();

  useEffect(() => {
    if (typeof window.__hidePreLoader === 'function') {
      window.__hidePreLoader();
    }
    const unsub = onAuthChange(async (u) => {
      setUser(u);
      if (u) {
        const token = await u.getIdTokenResult();
        setClaims(token.claims);
        loadForCurrentUser();
      } else {
        setClaims(null);
        clearAllCompanyData();
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  if (loading) {
    return (
      <div style={{
        display:'flex', alignItems:'center', justifyContent:'center',
        height:'100vh', background:'var(--black)',
        flexDirection:'column', gap:16,
      }}>
        <div style={{ fontFamily:'var(--font-display)', fontSize:24, color:'var(--gold-light)', letterSpacing:'0.02em' }}>
          InsightPro™
        </div>
        <div style={{ width:32, height:2, background:'var(--gold)', animation:'pulse 1.5s infinite' }} />
      </div>
    );
  }

  const isSuperAdmin = claims?.role === 'superAdmin';

  return (
    <BrowserRouter>
      <Routes>

        {/* ── Public landing page ── */}
        <Route path="/home" element={<LandingPage />} />
        <Route path="/legal" element={<LegalPage />} />

        {/* ── Public ── */}
        <Route path="/login" element={user ? <Navigate to="/select-company" replace /> : <Login />} />

        {/* ── Main app ── */}
        <Route path="/" element={user ? <Layout key={companyKey} user={user} claims={claims} /> : <LandingPage />}>
          <Route index                    element={<HomePage />} />
          <Route path="sales"             element={<SalesPage />} />
          <Route path="stock-optimisation" element={<StockOptimisation />} />
          <Route path="stock"             element={<StockPage />} />
          <Route path="debtors"           element={<DebtorsPage />} />
          <Route path="creditors"         element={<CreditorsPage />} />
          <Route path="branches"          element={<BranchPage />} />
          <Route path="advisory"          element={<Advisory />} />
          <Route path="actions"           element={<ActionTracking />} />
          <Route path="advisor"           element={<Advisor />} />
          <Route path="alerts"            element={<AlertPreferences />} />
          <Route path="group-pl"          element={<GroupPL />} />
          <Route path="store-performance" element={<StorePerformance />} />
          <Route path="balance-sheet"     element={<BalanceSheet />} />
          <Route path="expense-analysis"  element={<ExpenseAnalysis />} />
          <Route path="cash-flow"         element={<CashFlow />} />
          <Route path="notes"             element={<Notes />} />
          <Route path="group-consolidated" element={<GroupConsolidated />} />
          {/* QA Agent — superAdmin only */}
          <Route path="qa" element={isSuperAdmin ? <QAAgent /> : <Navigate to="/" replace />} />
          {/* Admin — superAdmin only */}
          <Route path="admin/clients"           element={isSuperAdmin ? <AdminClients />     : <Navigate to="/" replace />} />
          <Route path="admin/clients/:companyId" element={isSuperAdmin ? <AdminClientDetail /> : <Navigate to="/" replace />} />
          <Route path="admin/onboard"           element={isSuperAdmin ? <OnboardWizard />   : <Navigate to="/" replace />} />
        </Route>

        {/* ── Accept invite — public, no auth required ── */}
        <Route path="/accept-invite" element={<AcceptInvite />} />

        {/* ── Report renderer — supports shareToken (puppeteer) and signed-in (browser print) modes ── */}
        <Route path="/report-render" element={<ReportRender />} />

        {/* ── Company selector — requires auth ── */}
        <Route path="/select-company" element={user ? <CompanySelector /> : <Navigate to="/login" replace />} />

        <Route path="*" element={<Navigate to={user ? "/select-company" : "/home"} replace />} />

      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <CompanyProvider>
      <AppInner />
    </CompanyProvider>
  );
}
